<script setup lang="ts">
import { useRouter } from 'vue-router'

import AppLoadingScreen from '@/components/AppLoadingScreen.vue'
import DevCommandPalette from '@/components/DevCommandPalette.vue'
import DynamicImportFailureModal from '@/components/Modals/DynamicImportFailureModal.vue'
import AuthSessionSync from '@/components/Modals/AuthSessionSync.vue'
import { isResolvingInitialRoute } from '@/router/router'
import { useUiStore } from '@/stores/ui'
import { ANT_DESIGN_THEME } from '@/utilities/ant-design'
import { usePostHog } from '@/composables/posthog'
import { initPerformanceInstrumentation } from '@/utilities/performance'

const VITE_APP_DEMO_MENU_ACTIVE = import.meta.env.VITE_APP_DEMO_MENU_ACTIVE === 'true'

const router = useRouter()
const uiStore = useUiStore()
const { posthog, hasInitialised, initPosthog } = usePostHog()

if (!hasInitialised.value) {
  initPosthog()
}

if (hasInitialised.value) {
  initPerformanceInstrumentation({
    router,
    capture: (event, properties) => {
      if (hasInitialised.value) {
        posthog?.capture(event, properties)
      }
    },
  })
}

router.beforeEach((to, from) => {
  if (from.path !== to.path && hasInitialised.value) {
    posthog?.capture('$pageleave')
  }
})

router.afterEach(() => {
  if (hasInitialised.value) {
    posthog?.capture('$pageview')
  }
})
</script>

<template>
  <AppLoadingScreen v-if="isResolvingInitialRoute" />
  <a-config-provider v-else :theme="ANT_DESIGN_THEME">
    <AppLoadingScreen v-if="uiStore.isSessionTransitioning" />
    <router-view v-else />
    <AuthSessionSync />
  </a-config-provider>

  <DynamicImportFailureModal />

  <DevCommandPalette v-if="VITE_APP_DEMO_MENU_ACTIVE" />
</template>

<style lang="scss">
@use 'sass:map';
@use '@/styles/variables';
@use '@/styles/theme';
</style>
